
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * GatewayStatusNotify struct
 */
class GatewayStatusNotify {
  /**
   * Initialize struct
   * @param {!boolean=} isRemoteOnline
   * @constructor
   */
  constructor (isRemoteOnline = false) {
    this.IsRemoteOnline = isRemoteOnline
  }

  /**
   * Copy struct (shallow copy)
   * @this {!GatewayStatusNotify}
   * @param {!GatewayStatusNotify} other Other struct
   * @returns {!GatewayStatusNotify} This struct
   */
  copy (other) {
    if (other.IsRemoteOnline != null) {
      this.IsRemoteOnline = other.IsRemoteOnline
    } else {
      this.IsRemoteOnline = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!GatewayStatusNotify}
   * @returns {!GatewayStatusNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new GatewayStatusNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new GatewayStatusNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!GatewayStatusNotify}
   * @param {!GatewayStatusNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof GatewayStatusNotify)) {
      throw new TypeError('Instance of GatewayStatusNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!GatewayStatusNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      IsRemoteOnline: ((this.IsRemoteOnline != null) ? this.IsRemoteOnline : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return GatewayStatusNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!GatewayStatusNotify} other Object value
   * @returns {!GatewayStatusNotify} Created struct
   */
  static fromObject (other) {
    return new GatewayStatusNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!GatewayStatusNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return GatewayStatusNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!GatewayStatusNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 8010
  }
}

export { GatewayStatusNotify };

/**
 * Fast Binary Encoding GatewayStatusNotify field model
 */
class FieldModelGatewayStatusNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.IsRemoteOnline = new fbe.FieldModelBool(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelGatewayStatusNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelGatewayStatusNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.IsRemoteOnline.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelGatewayStatusNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.IsRemoteOnline.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelGatewayStatusNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelGatewayStatusNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelGatewayStatusNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 8010
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelGatewayStatusNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelGatewayStatusNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IsRemoteOnline.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsRemoteOnline.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsRemoteOnline.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelGatewayStatusNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelGatewayStatusNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelGatewayStatusNotify}
   * @param {!GatewayStatusNotify} fbeValue Default value, defaults is new GatewayStatusNotify()
   * @returns {!GatewayStatusNotify} GatewayStatusNotify value
   */
  get (fbeValue = new GatewayStatusNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelGatewayStatusNotify}
   * @param {!GatewayStatusNotify} fbeValue GatewayStatusNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IsRemoteOnline.fbeSize) <= fbeStructSize) {
      fbeValue.IsRemoteOnline = this.IsRemoteOnline.get()
    } else {
      fbeValue.IsRemoteOnline = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsRemoteOnline.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelGatewayStatusNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelGatewayStatusNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelGatewayStatusNotify}
   * @param {!GatewayStatusNotify} fbeValue GatewayStatusNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelGatewayStatusNotify}
   * @param {GatewayStatusNotify} fbeValue GatewayStatusNotify value
   */
  setFields (fbeValue) {
    this.IsRemoteOnline.set(fbeValue.IsRemoteOnline)
  }
}

export { FieldModelGatewayStatusNotify };

/**
 * Fast Binary Encoding GatewayStatusNotify model
 */
class GatewayStatusNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelGatewayStatusNotify(this.buffer, 4)
  }

  /**
   * Get the GatewayStatusNotify model
   * @this {!GatewayStatusNotifyModel}
   * @returns {!FieldModelGatewayStatusNotify} model GatewayStatusNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!GatewayStatusNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!GatewayStatusNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return GatewayStatusNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!GatewayStatusNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelGatewayStatusNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!GatewayStatusNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!GatewayStatusNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!GatewayStatusNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!GatewayStatusNotifyModel}
   * @param {!GatewayStatusNotify} value GatewayStatusNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!GatewayStatusNotifyModel}
   * @param {!GatewayStatusNotify} value GatewayStatusNotify value, defaults is new GatewayStatusNotify()
   * @return {!object} Deserialized GatewayStatusNotify value and its size
   */
  deserialize (value = new GatewayStatusNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new GatewayStatusNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new GatewayStatusNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!GatewayStatusNotifyModel}
   * @param {!number} prev Previous GatewayStatusNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { GatewayStatusNotifyModel };
